import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useTenant } from '../../context/TenantContext';
import * as api from '../../services/routing/api';
import { useHealthQuery, useTenantsQuery } from '../../services/routing/queries';
import BaseConfigPanel from './BaseConfigPanel';

const ENGINES = ['osrm', 'valhalla', 'myle', 'manual'];

export const AdminPage = () => {
  const { tenant: activeTenant, setTenant } = useTenant();
  const [newTenant, setNewTenant] = useState('');
  const [engine, setEngine] = useState('osrm');
  const [engineUrl, setEngineUrl] = useState('');
  const [checking, setChecking] = useState(false);
  const [check, setCheck] = useState<api.EngineCheck | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useTenantsQuery();
  const { data: health } = useHealthQuery();

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['tenants'] });
    queryClient.invalidateQueries({ queryKey: ['tenant-config'] });
  };

  const onCreate = async () => {
    const name = newTenant.trim();
    if (!name) {
      enqueueSnackbar('Enter a name for the new tenant');
      return;
    }
    try {
      await api.createTenant(name);
      enqueueSnackbar(`Tenant "${name}" created`);
      setNewTenant('');
      setTenant(name);
      refresh();
    } catch (createError) {
      enqueueSnackbar((createError as Error).message, { variant: 'error' });
    }
  };

  const onDelete = async (name: string) => {
    if (
      !window.confirm(
        `Delete tenant "${name}" entirely? Removes its stored config and its in-memory rides/routes.`
      )
    ) {
      return;
    }
    try {
      const result = await api.deleteTenant(name);
      enqueueSnackbar(`Tenant "${name}" deleted (${result.parameter_rules} rule(s))`);
      if (name === activeTenant) setTenant('default');
      refresh();
    } catch (deleteError) {
      enqueueSnackbar((deleteError as Error).message, { variant: 'error' });
    }
  };

  const onUse = (name: string) => {
    setTenant(name);
    enqueueSnackbar(`Now working with tenant "${name}"`);
  };

  const onCheckEngine = async () => {
    setChecking(true);
    setCheck(null);
    try {
      setCheck(await api.checkEngine(engine, engineUrl.trim()));
    } catch (checkError) {
      enqueueSnackbar((checkError as Error).message, { variant: 'error' });
    } finally {
      setChecking(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          TENANTS
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Every page works with the tenant picked in the top bar; its own settings live on the
          Config tab and its fleets on the World tab. Rides/routes counts are the in-memory world (cleared by a restart); config
          rows are durable.
        </Typography>
        {error && <Alert severity="error">{(error as Error).message}</Alert>}
        {isLoading && <CircularProgress aria-label="loading tenants" />}
        {data && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Parameter rules</TableCell>
                  <TableCell align="right">Rides</TableCell>
                  <TableCell align="right">Routes</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.details ?? []).map(row => (
                  <TableRow key={row.tenant} selected={row.tenant === activeTenant}>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{row.tenant}</TableCell>
                    <TableCell>{row.created_at ?? '(parameter rules only)'}</TableCell>
                    <TableCell align="right">{row.parameter_rules}</TableCell>
                    <TableCell align="right">{row.rides}</TableCell>
                    <TableCell align="right">{row.routes}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Button size="small" onClick={() => onUse(row.tenant)}>
                        Use
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        aria-label={`delete tenant ${row.tenant}`}
                        onClick={() => onDelete(row.tenant)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }} alignItems={{ xs: 'stretch', sm: 'center' }} useFlexGap>
          <TextField
            label="New tenant"
            size="small"
            value={newTenant}
            onChange={event => setNewTenant(event.target.value)}
          />
          <Button variant="contained" onClick={onCreate}>
            Create tenant
          </Button>
        </Stack>
      </Paper>

      <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 1 }}>
        OVERALL BASE CONFIG — EVERY TENANT PLANS ON TOP OF THIS
      </Typography>
      <BaseConfigPanel />

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          SERVICE HEALTH
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Every component behind the top-bar health chip. A DOWN component does not stop the
          service — the container probe reads the readiness group — but tells you what to restart:
          redis only costs the directions cache, db is the platform MySQL a source=database run
          reads.
        </Typography>
        {health && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Component</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontFamily: 'monospace' }}>overall</TableCell>
                  <TableCell>{health.status}</TableCell>
                  <TableCell />
                </TableRow>
                {Object.entries(health.components ?? {}).map(([name, component]) => (
                  <TableRow key={name}>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{name}</TableCell>
                    <TableCell>{component.status}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {String(component.details?.error ?? '')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {health && !health.components && (
          <Alert severity="info">
            The server is not reporting component details (management.endpoint.health.show-details).
          </Alert>
        )}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          ENGINE CONNECTIVITY
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Probes the engine with one short NY leg through the same stack a run uses. Leave the URL
          blank to test the deployment&apos;s configured URL, or paste a custom
          routing_system.*_url value before saving it.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            select
            size="small"
            label="Engine"
            value={engine}
            onChange={event => setEngine(event.target.value)}
            sx={{ minWidth: 140 }}
          >
            {ENGINES.map(name => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="URL (blank = deployment default)"
            value={engineUrl}
            onChange={event => setEngineUrl(event.target.value)}
            sx={{ minWidth: { xs: 0, sm: 320 }, flexGrow: 1 }}
          />
          <Button variant="contained" onClick={onCheckEngine} disabled={checking}>
            {checking ? 'Testing…' : 'Test connection'}
          </Button>
        </Stack>
        {check && (
          <Alert severity={check.reachable ? (check.routed ? 'success' : 'warning') : 'error'} sx={{ mt: 2 }}>
            {check.reachable
              ? check.routed
                ? `${check.engine} at ${check.url} answered in ${check.latency_ms} ms`
                : `${check.engine} at ${check.url} answered but could not route the NY probe leg — different map data?`
              : `${check.engine} at ${check.url} is unreachable — the probe fell back to the manual estimate`}
          </Alert>
        )}
      </Paper>
    </Stack>
  );
};

export default AdminPage;
